import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Clock, Wrench, XCircle, Factory,
  Building2, Users, TrendingUp, PieChart as PieIcon,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, ReferenceLine,
} from "recharts";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DateFilter from "../components/layout/DateFilter";

const DEPT_COLORS = {
  GFRC: "#A78BFA", FIBERGLASS: "#34D399",
  MOLD: "#00D4FF", FRAME: "#FBBF24", FINISH: "#EC4899",
};

const fmt = (n) => (n === null || n === undefined || isNaN(n) ? "—" : Number(n).toLocaleString());
const fmtSF = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Math.abs(Number(n));
  if (v >= 1000) return `${(Number(n) / 1000).toFixed(2)}K SQFT`;
  return `${fmt(n)} SQFT`;
};

export default function NCRDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role_name === "Administrator" || user?.role === "admin";
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = !canSeeMex ? false : (!canSeeSLC ? true : showMexicoState);

  return (
    <div className="space-y-6" data-testid="ncr-dashboard-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#F87171]">06 · NCR Dashboard</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">NCR Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            NCR HRS Sheet + Scrapped Report · {showMexico ? "MEXICO" : "SLC"} plant scope
          </p>
        </div>
        {canSeeSLC && canSeeMex ? (
          <button
            type="button" data-testid="ncr-toggle-mexico"
            onClick={() => setShowMexico((v) => !v)}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-mono border transition-colors ${
              showMexico ? "bg-[#FBBF24] text-[#0A0B10] border-[#FBBF24]" : "bg-[#12141D] text-muted-foreground border-border hover:text-white"
            }`}>
            {showMexico ? "Showing Mexico" : "Show Mexico Plants"}
          </button>
        ) : (
          <span className="px-3 py-2 text-[10px] tracking-widest uppercase font-mono border border-border bg-[#0A0B10] text-muted-foreground"
                data-testid="ncr-plant-scope-locked">
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </header>

      <SummaryCard showMexico={showMexico} />
      <ByDepartmentCard showMexico={showMexico} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ByStatusCard showMexico={showMexico} />
        <ByCauseCard  showMexico={showMexico} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ByPlantCard  showMexico={showMexico} />
        <WeeklyTrendCard showMexico={showMexico} />
      </div>

      <TopJobsCard showMexico={showMexico} />
      <ScrappedItemsSection showMexico={showMexico} />
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Shared card shell with date filter                                    */
/* --------------------------------------------------------------------- */
function useDashData(showMexico) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/ncr-dashboard", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  return { data, loading, filter, setFilter };
}

function CardShell({ title, icon, accent, filter, setFilter, children, testId, subtitle, actions }) {
  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden" data-testid={testId}>
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }} />
      <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-heading font-bold text-xl">{title}</h3>
          {subtitle && (
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">· {subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {actions}
          <DateFilter value={filter} onChange={setFilter} />
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

/* --------------------------------------------------------------------- */
/* Summary — 4 tiles (matches user screenshot)                           */
/* --------------------------------------------------------------------- */
function SummaryCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const s = data?.summary || {};
  return (
    <CardShell title="Summary" icon={<AlertTriangle size={18} className="text-[#F87171]" />}
               accent="#F87171" filter={filter} setFilter={setFilter} testId="ncr-summary-card">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryTile testid="ncr-sum-total"     label="Total NCRs"    value={fmt(s.total_ncrs)}     accent="#F87171"
          sub="Non-conformance reports" icon={<AlertTriangle size={14} />} />
        <SummaryTile testid="ncr-sum-hours"     label="Field Hours"   value={fmt(s.field_hours)}    accent="#FBBF24"
          sub="Total repair hours"      icon={<Clock size={14} />} />
        <SummaryTile testid="ncr-sum-repaired"  label="Repaired"      value={fmt(s.repaired_count)} accent="#34D399"
          sub={`${fmt(s.repaired_hours)} hrs`} icon={<Wrench size={14} />} />
        <SummaryTile testid="ncr-sum-scrapped"  label="Scrapped Items" value={fmt(s.scrapped_items)} accent="#EC4899"
          sub={`${fmtSF(s.scrapped_sqft)} total`} icon={<XCircle size={14} />} />
      </div>
    </CardShell>
  );
}

function SummaryTile({ testid, label, value, accent, sub, icon }) {
  return (
    <div className="relative bg-[#0A0B10] border border-border p-4 overflow-hidden" data-testid={testid}>
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{label}</div>
        <div className="p-1.5 bg-[#12141D] rounded-full border border-border/50" style={{ color: accent }}>
          {icon}
        </div>
      </div>
      <div className="font-heading font-bold text-4xl tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* NCR by Department                                                     */
/* --------------------------------------------------------------------- */
function ByDepartmentCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = data?.by_department || [];
  return (
    <CardShell title="NCR by Department" icon={<Building2 size={18} className="text-[#A78BFA]" />}
               accent="#A78BFA" filter={filter} setFilter={setFilter} testId="ncr-by-dept-card">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {rows.map((r) => (
          <div key={r.dept} data-testid={`ncr-dept-${r.dept}`}
               className="bg-[#0A0B10] border border-border p-4 text-center">
            <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: DEPT_COLORS[r.dept] || "#94A3B8" }}>
              {r.dept}
            </div>
            <div className="font-heading font-bold text-3xl tabular-nums mt-1">{fmt(r.count)}</div>
            <div className="text-xs text-muted-foreground mt-1">{fmt(r.hours)}h</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">
            No NCRs in this date range
          </div>
        )}
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* NCR Count by Status  (donut + legend)                                 */
/* --------------------------------------------------------------------- */
function ByStatusCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = data?.by_status || [];
  return (
    <CardShell title="NCR Count by Status" icon={<PieIcon size={18} className="text-[#34D399]" />}
               accent="#34D399" filter={filter} setFilter={setFilter} testId="ncr-by-status-card">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="h-[280px]">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={rows} dataKey="count" nameKey="status" innerRadius={65} outerRadius={100} paddingAngle={2}>
                {rows.map((r, i) => <Cell key={i} fill={r.colour || "#94A3B8"} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }}
                       labelStyle={{ color: "#94A3B8" }} formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.status} className="flex items-center justify-between border border-border/40 bg-[#0A0B10] px-3 py-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.colour }} />
                <span>{r.status}</span>
              </div>
              <span className="font-mono tabular-nums text-white">{fmt(r.count)}</span>
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* NCR Count by Cause  (horizontal bar)                                  */
/* --------------------------------------------------------------------- */
function ByCauseCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = (data?.by_cause || []).slice(0, 10);
  return (
    <CardShell title="NCR Count by Cause" icon={<TrendingUp size={18} className="text-[#F87171]" />}
               accent="#F87171" filter={filter} setFilter={setFilter} testId="ncr-by-cause-card">
      <div className="h-[280px]">
        <ResponsiveContainer>
          <BarChart data={rows} layout="vertical" margin={{ left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis type="number" stroke="#94A3B8" fontSize={11} />
            <YAxis type="category" dataKey="cause" stroke="#94A3B8" fontSize={11} width={140} />
            <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} formatter={(v) => fmt(v)} />
            <Bar dataKey="count" fill="#F87171" radius={[0, 4, 4, 0]} label={{ position: "right", fill: "#94A3B8", fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* NCR Count by Plant  (horizontal bar)                                  */
/* --------------------------------------------------------------------- */
function ByPlantCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = (data?.by_plant || []).slice(0, 10);
  return (
    <CardShell title="NCR Count by Plant" icon={<Factory size={18} className="text-[#F87171]" />}
               accent="#F87171" filter={filter} setFilter={setFilter} testId="ncr-by-plant-card">
      <div className="h-[280px]">
        <ResponsiveContainer>
          <BarChart data={rows} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis type="number" stroke="#94A3B8" fontSize={11} />
            <YAxis type="category" dataKey="plant" stroke="#94A3B8" fontSize={11} width={140}
                   tickFormatter={(v) => v.length > 18 ? v.slice(0, 17) + "…" : v} />
            <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} formatter={(v) => fmt(v)} />
            <Bar dataKey="count" fill="#F87171" radius={[0, 4, 4, 0]} label={{ position: "right", fill: "#94A3B8", fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* Weekly Trend  (count + hours line)                                    */
/* --------------------------------------------------------------------- */
function WeeklyTrendCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = data?.weekly_trend || [];
  return (
    <CardShell title="NCR Weekly Trend" icon={<TrendingUp size={18} className="text-[#FBBF24]" />}
               accent="#FBBF24" filter={filter} setFilter={setFilter} testId="ncr-weekly-trend-card">
      <div className="h-[280px]">
        <ResponsiveContainer>
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="week" stroke="#94A3B8" fontSize={10} />
            <YAxis stroke="#94A3B8" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} formatter={(v) => fmt(v)} />
            <Line type="monotone" dataKey="count" stroke="#EC4899" strokeWidth={2} dot={{ fill: "#EC4899", r: 3 }} name="NCRs" />
            <Line type="monotone" dataKey="hours" stroke="#FBBF24" strokeWidth={2} dot={{ fill: "#FBBF24", r: 3 }} name="Field Hours" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* Top Jobs by NCR Count  (table)                                        */
/* --------------------------------------------------------------------- */
function TopJobsCard({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const rows = (data?.top_jobs || []).slice(0, 25);
  return (
    <CardShell title="Top Jobs by NCR Count" icon={<Users size={18} className="text-[#00D4FF]" />}
               accent="#00D4FF" filter={filter} setFilter={setFilter} testId="ncr-top-jobs-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-2 px-3">Job</th>
              <th className="py-2 px-3 text-right">NCRs</th>
              <th className="py-2 px-3 text-right">Field Hours</th>
              <th className="py-2 px-3 text-right">Scrapped</th>
              <th className="py-2 px-3 text-right">Repaired</th>
            </tr>
          </thead>
          <tbody data-testid="ncr-top-jobs-rows">
            {rows.map((r, i) => (
              <tr key={r.job || i} className="border-b border-border/40 hover:bg-[#0A0B10]/40">
                <td className="py-2 px-3">{r.job}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.count)}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.hours)}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.scrapped_sqft)}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.repaired)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">No jobs</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

/* --------------------------------------------------------------------- */
/* Scrapped Items — big section with 4 charts                             */
/* --------------------------------------------------------------------- */
function ScrappedItemsSection({ showMexico }) {
  const { data, filter, setFilter } = useDashData(showMexico);
  const scrapped = data?.scrapped || {};
  const byJob   = (scrapped.by_job   || []).slice(0, 10);
  const byPlant = (scrapped.by_plant || []).slice(0, 10);
  const weekly  = scrapped.weekly_trend || [];
  const byCross = (scrapped.by_cross_section || []).slice(0, 10);

  return (
    <CardShell
      title="Scrapped Items"
      icon={<XCircle size={18} className="text-[#F87171]" />}
      accent="#F87171"
      filter={filter} setFilter={setFilter}
      testId="ncr-scrapped-section"
      subtitle={`${fmt(scrapped.total_items)} items · ${fmtSF(scrapped.total_sqft)} scrapped`}
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SubChartBar title="Scrapped SQFT by Job"   testId="scrap-by-job"   rows={byJob}   dataKey="job"           colour="#F87171" />
        <SubChartBar title="Scrapped SQFT by Plant" testId="scrap-by-plant" rows={byPlant} dataKey="plant"         colour="#FBBF24" width={110} />
        <SubChartWeek title="Scrapped Trend by Week" testId="scrap-weekly" rows={weekly} />
        <SubChartBar title="Scrapped by Cross Section" testId="scrap-by-cross" rows={byCross} dataKey="cross_section" colour="#A78BFA" width={110} />
      </div>
    </CardShell>
  );
}

function SubChartBar({ title, testId, rows, dataKey, colour, width = 90 }) {
  return (
    <div className="bg-[#0A0B10] border border-border p-4" data-testid={testId}>
      <h4 className="font-heading font-semibold mb-3">{title}</h4>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <BarChart data={rows} layout="vertical" margin={{ left: width }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis type="number" stroke="#94A3B8" fontSize={11} />
            <YAxis type="category" dataKey={dataKey} stroke="#94A3B8" fontSize={11} width={width + 20}
                   tickFormatter={(v) => (v && v.length > 14 ? v.slice(0, 13) + "…" : v)} />
            <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} formatter={(v) => `${fmt(v)} SF`} />
            <Bar dataKey="sqft" fill={colour} radius={[0, 4, 4, 0]}
                 label={{ position: "right", fill: "#94A3B8", fontSize: 10, formatter: (v) => `${fmt(v)} SF` }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SubChartWeek({ title, testId, rows }) {
  return (
    <div className="bg-[#0A0B10] border border-border p-4" data-testid={testId}>
      <h4 className="font-heading font-semibold mb-3">{title}</h4>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="week" stroke="#94A3B8" fontSize={10} />
            <YAxis stroke="#94A3B8" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #333" }} formatter={(v) => `${fmt(v)} SF`} />
            <Bar dataKey="sqft" fill="#F87171" radius={[4, 4, 0, 0]}
                 label={{ position: "top", fill: "#94A3B8", fontSize: 10, formatter: (v) => fmt(v) }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
